import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { useOnboardingContext } from './OnboardingContext';
import { StepModal } from './StepModal';
import { useOnboardingTranslation } from '../../translations';
import { getOnboardingColors } from './colors';

const useStyles = makeStyles(theme => {
  const c = getOnboardingColors(theme);
  return {
    exitDialog: {
      '& .MuiDialog-paper': {
        borderRadius: 8,
        overflow: 'hidden',
      },
    },
    exitTitle: {
      fontSize: '1rem',
      lineHeight: '1.25rem',
      fontWeight: 700,
      padding: theme.spacing(2.5, 3, 1),
    },
    exitContent: {
      padding: theme.spacing(1, 3, 2),
    },
    exitMessage: {
      fontSize: '0.875rem',
      lineHeight: '1.375rem',
      color: theme.palette.text.secondary,
    },
    exitActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 2, 2),
    },
    leaveButton: {
      fontWeight: 600,
      textTransform: 'none',
      borderRadius: 4,
      padding: theme.spacing(1, 2),
      borderColor: theme.palette.divider,
    },
    stayButton: {
      fontWeight: 600,
      textTransform: 'none',
      borderRadius: 4,
      padding: theme.spacing(1, 2),
      color: c.buttonPrimaryFg,
    },
  };
});

/**
 * Renders the StepModal spotlight overlay when an onboarding session is active.
 * Place this once near the root of the app — it looks for the target element
 * via `targetSelector` (a CSS selector) on each step.
 */
export const OnboardingOverlay = () => {
  const classes = useStyles();
  const {
    session,
    currentStep,
    nextStep,
    prevStep,
    endSession,
    completeSession,
  } = useOnboardingContext();
  const { t } = useOnboardingTranslation();
  const navigate = useNavigate();
  const proxyRef = useRef<HTMLElement | null>(null);
  const [, forceRender] = useState(0);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const resolveTarget = useCallback(() => {
    if (!currentStep?.targetSelector) {
      proxyRef.current = null;
      forceRender(n => n + 1);
      return;
    }
    const el = document.querySelector<HTMLElement>(currentStep.targetSelector);
    proxyRef.current = el;
    forceRender(n => n + 1);
  }, [currentStep]);

  // Navigate to the required route when a step specifies navigateTo
  useEffect(() => {
    if (!currentStep?.navigateTo) return;
    // Only navigate if we're not already on the target path
    if (window.location.pathname !== currentStep.navigateTo) {
      navigate(currentStep.navigateTo);
    }
  }, [currentStep, navigate]);

  useEffect(() => {
    if (!currentStep) return () => {};

    const initialDelay = currentStep.navigateTo ? 300 : 100;
    const pollInterval = 250;

    const initialTimer = setTimeout(() => {
      resolveTarget();
    }, initialDelay);

    const intervalId = setInterval(resolveTarget, pollInterval);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalId);
    };
  }, [currentStep, resolveTarget]);

  if (!session || !currentStep) return null;

  const isLastStep = session.currentStepIndex === session.steps.length - 1;

  const nextStep_ = !isLastStep
    ? session.steps[session.currentStepIndex + 1]
    : undefined;
  const nextStepIsQuiz = nextStep_?.stepType === 'quiz';
  const nextStepIsPlayground = nextStep_?.stepType === 'playground';

  const getContinueLabel = () => {
    if (nextStepIsQuiz) return t('quizStartQuiz');
    if (nextStepIsPlayground) return t('playgroundGoToPlayground');
    if (isLastStep) return t('finish');
    return t('continueLabel');
  };

  const handleContinue = () => {
    if (isLastStep) {
      const returnPath = session.returnPath;
      completeSession();
      navigate(returnPath);
    } else {
      nextStep();
    }
  };

  const handleClose = () => {
    setShowExitConfirm(true);
  };

  const handleConfirmLeave = () => {
    setShowExitConfirm(false);
    const returnPath = session.returnPath;
    endSession();
    navigate(returnPath);
  };

  const handleCancelLeave = () => {
    setShowExitConfirm(false);
  };

  const handleBack =
    session.currentStepIndex > 0 ? () => prevStep() : undefined;

  // Build a stable ref object pointing to the resolved element
  const targetRefObject = currentStep.targetSelector
    ? { current: proxyRef.current }
    : undefined;

  return (
    <>
      <StepModal
        open
        onClose={handleClose}
        journeyTitle={currentStep.journeyTitle}
        sectionTitle={currentStep.sectionTitle}
        currentStep={currentStep.stepIndex}
        totalSteps={currentStep.totalSteps}
        stepTitle={currentStep.stepTitle}
        stepIndicatorLabel={t('stepIndicator', {
          current: currentStep.stepIndex,
          total: currentStep.totalSteps,
        })}
        onBack={currentStep.customActions ? undefined : handleBack}
        onContinue={currentStep.customActions ? undefined : handleContinue}
        continueLabel={getContinueLabel()}
        backLabel={t('back')}
        hideActions={currentStep.customActions}
        hideSteps={currentStep.customActions}
        hideStepTitle={currentStep.customActions || currentStep.hideStepTitle}
        centerContent={currentStep.centerContent}
        targetRef={targetRefObject as React.RefObject<HTMLElement> | undefined}
        placement={currentStep.placement ?? 'left'}
        spotlightPadding={12}
      >
        {currentStep.stepContent}
      </StepModal>

      <Dialog
        open={showExitConfirm}
        onClose={handleCancelLeave}
        maxWidth="xs"
        fullWidth
        className={classes.exitDialog}
        style={{ zIndex: 1400 }}
      >
        <Typography className={classes.exitTitle}>
          {t('exitConfirmTitle')}
        </Typography>
        <DialogContent className={classes.exitContent}>
          <Typography className={classes.exitMessage}>
            {t('exitConfirmMessage')}
          </Typography>
        </DialogContent>
        <DialogActions className={classes.exitActions}>
          <Button
            onClick={handleConfirmLeave}
            color="primary"
            variant="outlined"
            className={classes.leaveButton}
          >
            {t('exitConfirmLeave')}
          </Button>
          <Button
            onClick={handleCancelLeave}
            variant="contained"
            color="primary"
            className={classes.stayButton}
          >
            {t('exitConfirmStay')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
